import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { client, unwrap, type Schemas } from '../api/client';
import { tokens, type Session } from './tokens';
import { clearAllDrafts } from '../lib/draft-storage';

export type Me = Schemas['MeDto'];
type Status = 'loading' | 'anonymous' | 'authenticated';

interface AuthContextValue {
  status: Status;
  session: Session | null;
  me: Me | undefined;
  login: (email: string, password: string, mfaCode?: string) => Promise<Session>;
  signup: (input: Schemas['SignupDto']) => Promise<Session>;
  acceptSession: (session: Session) => void;
  switchTenant: (tenantId: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const meQueryKey = ['auth', 'me'] as const;

/**
 * Al pulsar «Salir» no se recuerda la pantalla actual para volver a ella al ingresar: la siguiente
 * persona en ingresar (otra cuenta) caía en la última pantalla de la anterior. Solo se recuerda
 * cuando la sesión termina sola (venció) y la misma persona vuelve a entrar.
 */
let explicitLogout = false;
export function loggedOutOnPurpose(): boolean {
  return explicitLogout;
}
export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [session, setSession] = useState<Session | null>(null);
  // Si ya hay access token en memoria (p. ej. tras recargar módulos) la sesión está activa; si hay
  // marca de sesión (la cookie HttpOnly no se puede leer), se restaura; sin ninguna, anónimo.
  const [status, setStatus] = useState<Status>(() =>
    tokens.access ? 'authenticated' : tokens.mayHaveSession ? 'loading' : 'anonymous',
  );

  useEffect(() => {
    const unsubscribe = tokens.subscribe((next) => {
      setSession(next);
      setStatus(next ? 'authenticated' : 'anonymous');
      if (next) explicitLogout = false;
      if (!next) queryClient.clear();
    });
    // Restaurar la sesión tras recargar la página (el access token solo vive en memoria).
    if (tokens.mayHaveSession && !tokens.access) void tokens.refresh();
    return unsubscribe;
  }, [queryClient]);

  const me = useQuery({
    queryKey: [...meQueryKey, session?.tenantId],
    queryFn: () => unwrap<Me>(client.GET('/v1/auth/me')),
    enabled: status === 'authenticated',
    staleTime: 60_000,
  });

  const login = useCallback(async (email: string, password: string, mfaCode?: string) => {
    const result = await unwrap<Session>(
      client.POST('/v1/auth/login', { body: { email, password, ...(mfaCode ? { mfaCode } : {}) } }),
    );
    tokens.setSession(result);
    return result;
  }, []);

  const signup = useCallback(async (input: Schemas['SignupDto']) => {
    const result = await unwrap<Session>(client.POST('/v1/auth/signup', { body: input }));
    tokens.setSession(result);
    return result;
  }, []);

  const acceptSession = useCallback((next: Session) => tokens.setSession(next), []);

  const switchTenant = useCallback(
    async (tenantId: string) => {
      const result = await unwrap<Session>(
        client.POST('/v1/auth/switch-tenant', { body: { tenantId } }),
      );
      queryClient.clear();
      tokens.setSession(result);
    },
    [queryClient],
  );

  const logout = useCallback(async () => {
    explicitLogout = true;
    await client.POST('/v1/auth/logout').catch(() => undefined);
    // Los borradores pueden tener mensajes de clientes: no se quedan en un equipo compartido.
    clearAllDrafts();
    tokens.clear();
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ status, session, me: me.data, login, signup, acceptSession, switchTenant, logout }),
    [status, session, me.data, login, signup, acceptSession, switchTenant, logout],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth debe usarse dentro de AuthProvider');
  return context;
}

/** Producto que contrató la empresa («EATHERIX CRM», «EATHERIX ERP» o «EATHERIX Sales»). */
export function useProductName(): string {
  return useAuth().me?.tenant.productName ?? 'EATHERIX';
}
